import { useState, useEffect, useCallback, useMemo } from 'react'
import { AdminAlert } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import {
  adminGetSeasonStats,
  adminGetSeasonConversionConfig,
  adminSaveSeasonConversionConfig,
} from '../../../services/admin'

const DEFAULT_RATES = [40, 80, 100, 200]

function toNumber(v) {
  const n = Number(String(v).replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}

function fmt(n, opts = {}) {
  return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2, ...opts })
}

function fmtMoney(n) {
  return `$${fmt(n)}`
}

function fmtDate(d) {
  if (!d) return '—'
  const date = new Date(d)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

export function SeasonDetail({ season, onBack, onEndSeason, onActivateSeason }) {
  if (!season) {
    return (
      <div className="flex flex-col gap-4">
        <BackBar onBack={onBack} />
        <AdminAlert type="error" message="Season not found." />
      </div>
    )
  }

  const isEnded = season.status === 'ended'

  return (
    <div className="flex flex-col gap-6">
      <BackBar onBack={onBack} />

      <div className="border-4 border-on-background bg-surface rounded-lg p-5 flex items-start justify-between gap-4 flex-wrap"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-extrabold text-[18px]">{season.seasonName}</h2>
            <span className={`px-2 py-0.5 border-2 text-[10px] font-bold uppercase tracking-widest rounded ${isEnded ? 'border-on-background/30 bg-surface-container text-on-surface-variant' : 'border-primary-container bg-primary-container/20 text-on-primary-container'}`}>
              {season.status ?? 'upcoming'}
            </span>
          </div>
          {season.description && <p className="text-[12px] text-on-surface-variant">{season.description}</p>}
          <p className="text-[12px] text-on-surface-variant font-mono">Started {fmtDate(season.startDate)}</p>
        </div>
        <div className="flex gap-2">
          {season.status === 'upcoming' && (
            <button onClick={onActivateSeason}
              className="px-4 py-2 border-4 border-on-background bg-primary-container text-on-primary-fixed font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-all"
              style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}>
              Activate
            </button>
          )}
          {!isEnded && (
            <button onClick={onEndSeason}
              className="px-4 py-2 border-4 border-on-background bg-surface font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors"
              style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}>
              End Season
            </button>
          )}
        </div>
      </div>

      {!isEnded ? (
        <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-10 flex flex-col items-center gap-3 text-center">
          <span className="material-symbols-outlined text-[32px] text-on-surface-variant">lock</span>
          <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">
            Conversion setup and payout unlock once this season is ended
          </p>
          <p className="text-[12px] text-on-surface-variant max-w-md">
            There's no fixed end date — this season stays open until you end it yourself. Once ended, you'll be able
            to set a CIS→CPT conversion rate here, then upload the final leaderboard snapshot.
          </p>
        </div>
      ) : (
        <EndedSeasonTools seasonId={season.id} />
      )}
    </div>
  )
}

function BackBar({ onBack }) {
  return (
    <button onClick={onBack}
      className="self-start flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-on-surface-variant hover:text-on-surface transition-colors">
      <span className="material-symbols-outlined text-[16px]">arrow_back</span>
      All Seasons
    </button>
  )
}

// ─── Everything that only makes sense once a season has ended ────────────
function EndedSeasonTools({ seasonId }) {
  // Step 1: real period stats, to pre-fill the simulator
  const [seasonStats, setSeasonStats] = useState(null)
  const [statsLoading, setStatsLoading] = useState(true)

  // Step 2: conversion config (saved or draft)
  const [savedConfig, setSavedConfig] = useState(null)
  const [configLoading, setConfigLoading] = useState(true)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    setStatsLoading(true)
    adminGetSeasonStats(seasonId)
      .then(setSeasonStats)
      .catch(() => setSeasonStats(null))
      .finally(() => setStatsLoading(false))
  }, [seasonId])

  const loadConfig = useCallback(() => {
    setConfigLoading(true)
    adminGetSeasonConversionConfig(seasonId)
      .then(setSavedConfig)
      .catch(() => setSavedConfig(null))
      .finally(() => setConfigLoading(false))
  }, [seasonId])

  useEffect(() => { loadConfig() }, [loadConfig])

  return (
    <div className="flex flex-col gap-6">
      {message && <AdminAlert type="success" message={message} />}
      {error && <AdminAlert type="error" message={error} />}

      <ConversionStep
        seasonId={seasonId}
        seasonStats={seasonStats}
        statsLoading={statsLoading}
        savedConfig={savedConfig}
        configLoading={configLoading}
        onSaved={(cfg) => {
          setSavedConfig(cfg)
          setMessage('Conversion rate saved for this season.')
          setTimeout(() => setMessage(null), 5000)
        }}
        onError={(msg) => setError(msg)}
      />

      <div className="border-4 border-on-background bg-surface-container-low p-5 flex items-start gap-3"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <span className="material-symbols-outlined text-[20px] text-on-surface-variant">arrow_forward</span>
        <p className="text-[12px] text-on-surface-variant">
          Ready to pay this season out? Snapshot upload, payout tiers, wallet registration tracking, and CSV export
          now live in the <span className="font-bold text-on-surface">Season Payouts</span> tab — no CIS conversion
          step required there.
        </p>
      </div>
    </div>
  )
}

// ─── Step 1: Conversion Simulator, scoped to this season, saveable ───────
function ConversionStep({ seasonId, seasonStats, statsLoading, savedConfig, configLoading, onSaved, onError }) {
  const [editing, setEditing] = useState(false)

  const [cisEarnedPeriod, setCisEarnedPeriod] = useState('0')
  const [platformRevenue, setPlatformRevenue] = useState('0')
  const [surplusFunds, setSurplusFunds] = useState('0')
  const [safeBudgetDollars, setSafeBudgetDollars] = useState('0')
  const [cptDollarRate, setCptDollarRate] = useState('0.0077')

  const [rates, setRates] = useState(DEFAULT_RATES)
  const [customRate, setCustomRate] = useState('')
  const [rateError, setRateError] = useState(null)
  const [selectedRate, setSelectedRate] = useState(null)
  const [saving, setSaving] = useState(false)

  // Pre-fill from real season stats once they arrive (only if nothing's
  // been saved yet — don't clobber a saved config's inputs on reload).
  useEffect(() => {
    if (!seasonStats || savedConfig) return
    if (seasonStats.cisEarnedPeriod != null) setCisEarnedPeriod(String(seasonStats.cisEarnedPeriod))
    if (seasonStats.platformRevenue != null) setPlatformRevenue(String(seasonStats.platformRevenue))
  }, [seasonStats, savedConfig])

  // If a config was already saved, load it into the form for reference /
  // re-saving.
  useEffect(() => {
    if (!savedConfig) return
    setCisEarnedPeriod(String(savedConfig.cisEarnedPeriod ?? '0'))
    setPlatformRevenue(String(savedConfig.platformRevenue ?? '0'))
    setSurplusFunds(String(savedConfig.surplusFunds ?? '0'))
    setSafeBudgetDollars(String(savedConfig.safeBudgetDollars ?? '0'))
    setCptDollarRate(String(savedConfig.cptDollarRate ?? '0.0077'))
    setSelectedRate(savedConfig.rate ?? null)
  }, [savedConfig])

  const cisEarnedNum = toNumber(cisEarnedPeriod)
  const surplusNum = toNumber(surplusFunds)
  const safeBudgetDollarsNum = toNumber(safeBudgetDollars)
  const cptDollarRateNum = toNumber(cptDollarRate)
  const convertibleCis = cisEarnedNum
  const budgetNum = cptDollarRateNum > 0 ? safeBudgetDollarsNum / cptDollarRateNum : 0

  const rows = useMemo(() => {
    return [...rates]
      .sort((a, b) => a - b)
      .map((rate) => {
        const projectedCpt = rate > 0 ? convertibleCis / rate : 0
        const pctOfBudget = budgetNum > 0 ? (projectedCpt / budgetNum) * 100 : null
        let status = 'unknown'
        if (pctOfBudget !== null) {
          if (pctOfBudget <= 85) status = 'safe'
          else if (pctOfBudget <= 100) status = 'tight'
          else status = 'over'
        }
        return { rate, projectedCpt, pctOfBudget, status }
      })
  }, [rates, convertibleCis, budgetNum])

  const statusMeta = {
    safe: { label: 'Safe', dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
    tight: { label: 'Tight', dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
    over: { label: 'Over Budget', dot: 'bg-error', text: 'text-error', bg: 'bg-error-container/40', border: 'border-error/30' },
    unknown: { label: 'Set a budget to compare', dot: 'bg-on-surface-variant/40', text: 'text-on-surface-variant', bg: 'bg-surface-container', border: 'border-on-background/10' },
  }

  const addRate = () => {
    const n = toNumber(customRate)
    setRateError(null)
    if (!n || n <= 0) { setRateError('Enter a positive number of CIS per 1 CPT'); return }
    if (rates.includes(n)) { setRateError('That rate is already in the list'); return }
    if (rates.length >= 10) { setRateError('Remove a rate before adding another (max 10)'); return }
    setRates((r) => [...r, n])
    setCustomRate('')
  }

  const removeRate = (rate) => {
    setRates((r) => r.filter((x) => x !== rate))
    if (selectedRate === rate) setSelectedRate(null)
  }

  const selectedRow = rows.find((r) => r.rate === selectedRate) ?? null

  const saveConversion = async () => {
    if (!selectedRow) { onError('Select a candidate rate as the draft rate first.'); return }
    setSaving(true)
    onError(null)
    try {
      const payload = {
        rate: selectedRow.rate,
        cisEarnedPeriod: cisEarnedNum,
        platformRevenue: toNumber(platformRevenue),
        surplusFunds: surplusNum,
        safeBudgetDollars: safeBudgetDollarsNum,
        cptDollarRate: cptDollarRateNum,
        projectedCpt: selectedRow.projectedCpt,
      }
      const saved = await adminSaveSeasonConversionConfig(seasonId, payload)
      onSaved({ ...payload, ...saved })
      setEditing(false)
    } catch (err) {
      onError(err.message ?? 'Failed to save conversion rate')
    } finally {
      setSaving(false)
    }
  }

  const loading = statsLoading || configLoading

  // ── Collapsed summary once a config is saved and not being edited ──
  if (loading) {
    return (
      <div className="border-4 border-on-background bg-surface p-5" style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <Skeleton className="h-24 w-full rounded" />
      </div>
    )
  }

  if (savedConfig?.rate && !editing) {
    return (
      <div className="border-4 border-primary-container bg-primary-container/10 p-5 flex flex-col gap-3"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-extrabold text-[13px] uppercase tracking-widest">✓ Conversion Rate Saved</h3>
          <button onClick={() => setEditing(true)}
            className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant hover:text-on-surface">
            Edit / Re-simulate
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-[12px]">
          <div><p className="text-on-surface-variant text-[10px] uppercase tracking-widest font-bold">Rate</p><p className="font-mono font-bold">{savedConfig.rate} CIS : 1 CPT</p></div>
          <div><p className="text-on-surface-variant text-[10px] uppercase tracking-widest font-bold">Projected Payout</p><p className="font-mono font-bold">{fmt(savedConfig.projectedCpt)} CPT</p></div>
          <div><p className="text-on-surface-variant text-[10px] uppercase tracking-widest font-bold">$/CPT</p><p className="font-mono font-bold">{fmtMoney(savedConfig.cptDollarRate)}</p></div>
          <div><p className="text-on-surface-variant text-[10px] uppercase tracking-widest font-bold">Safe Budget</p><p className="font-mono font-bold">{fmtMoney(savedConfig.safeBudgetDollars)}</p></div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="border-4 border-on-background bg-surface-container-low p-5 flex flex-col gap-1"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant">Step 1 — Conversion Simulator</p>
        <p className="text-[12px] text-on-surface-variant">
          Pre-filled with this season's real CIS/CPT totals where available. Compare candidate rates, pick one, then
          Save — this unlocks payout setup below. Nothing here touches real user balances until it's saved.
        </p>
        {!seasonStats && (
          <p className="text-[11px] text-amber-700 mt-1">
            Couldn't load real period totals for this season (backend endpoint not available yet) — figures below are
            editable manually.
          </p>
        )}
      </div>

      {/* ── Season Totals — read-only, platform-computed, not admin-entered ── */}
      <div className="border-4 border-on-background bg-surface p-5 flex flex-col gap-4"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <h3 className="font-extrabold text-[13px] uppercase tracking-widest">Season Totals</h3>
        {statsLoading ? (
          <Skeleton className="h-16 w-full rounded" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <StatDisplay label="Total CIS Held (platform-wide)" value={`${fmt(seasonStats?.totalCisHeld ?? 0)} CIS`} />
            <StatDisplay label="Total CPT Earned This Season" value={`${fmt(seasonStats?.totalCptEarned ?? 0)} CPT`} />
            <StatDisplay label="Active Users This Season" value={fmt(seasonStats?.activeUsers ?? 0)} />
          </div>
        )}
      </div>

      <div className="border-4 border-on-background bg-surface p-5 flex flex-col gap-4"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <h3 className="font-extrabold text-[13px] uppercase tracking-widest">Period Inputs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SimField label="CIS earned by active users this season" value={cisEarnedPeriod} onChange={setCisEarnedPeriod} suffix="CIS" />
          <SimField label="Platform revenue this season" value={platformRevenue} onChange={setPlatformRevenue} suffix="$" />
          <SimField label="Available surplus / reward funds" value={surplusFunds} onChange={setSurplusFunds} suffix="$" />
          <SimField label="$ value of 1 CPT" value={cptDollarRate} onChange={setCptDollarRate} suffix="$/CPT" />
          <div className="sm:col-span-2 flex flex-col gap-1">
            <SimField label="Safe reward budget" value={safeBudgetDollars} onChange={setSafeBudgetDollars} suffix="$"
              note={`≈ ${fmt(budgetNum)} CPT at the current $/CPT rate — projected payouts below are compared against this.`} />
            <button
              onClick={() => setSafeBudgetDollars(surplusFunds)}
              className="self-start text-[10px] font-bold uppercase tracking-widest text-primary-container hover:opacity-80 transition-opacity">
              Use available surplus funds ({fmtMoney(surplusNum)})
            </button>
          </div>
        </div>
      </div>

      <div className="border-4 border-on-background bg-surface p-5 flex flex-col gap-4"
        style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-extrabold text-[13px] uppercase tracking-widest">Candidate Conversion Rates</h3>
          <div className="flex items-center gap-2">
            <input
              type="number" min="1" placeholder="e.g. 120"
              value={customRate} onChange={(e) => setCustomRate(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addRate() }}
              className="w-28 bg-surface border-2 border-on-background px-3 py-2 font-mono text-[13px] focus:outline-none focus:border-primary-container"
            />
            <button onClick={addRate}
              className="px-3 py-2 border-2 border-on-background bg-on-background text-surface font-bold text-[10px] uppercase tracking-widest hover:opacity-90 transition-opacity">
              + Add Rate
            </button>
          </div>
        </div>
        {rateError && <AdminAlert type="error" message={rateError} />}
        <div className="flex flex-wrap gap-2">
          {[...rates].sort((a, b) => a - b).map((rate) => (
            <span key={rate}
              className="flex items-center gap-2 border-2 border-on-background bg-surface-container px-3 py-1.5 font-mono font-bold text-[12px]">
              {rate} CIS = 1 CPT
              <button onClick={() => removeRate(rate)} className="text-on-surface-variant hover:text-error transition-colors">
                <span className="material-symbols-outlined text-[14px] block">close</span>
              </button>
            </span>
          ))}
          {rates.length === 0 && <p className="text-[12px] text-on-surface-variant">No candidate rates yet — add one above.</p>}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <h3 className="font-extrabold text-[13px] uppercase tracking-widest px-1">Projected Payout Per Rate</h3>
        {rows.length === 0 ? (
          <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-10 text-center">
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">Add a candidate rate to see projections</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rows.map(({ rate, projectedCpt, pctOfBudget, status }) => {
              const meta = statusMeta[status]
              const isSelected = selectedRate === rate
              return (
                <div key={rate}
                  className={`border-4 bg-surface p-4 flex flex-col gap-3 transition-colors ${isSelected ? 'border-primary-container' : 'border-on-background'}`}
                  style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-extrabold text-[15px]">{rate} CIS = 1 CPT</p>
                      <p className="text-[11px] text-on-surface-variant">
                        {fmt(convertibleCis)} CIS ÷ {rate} = <span className="font-mono font-bold text-on-surface">{fmt(projectedCpt)} CPT</span>
                        {cptDollarRateNum > 0 && <span className="text-on-surface-variant"> (≈ {fmtMoney(projectedCpt * cptDollarRateNum)})</span>}
                      </p>
                    </div>
                    <span className={`flex items-center gap-1.5 border px-2 py-1 text-[9px] font-bold uppercase tracking-widest whitespace-nowrap ${meta.bg} ${meta.text} ${meta.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                      {meta.label}
                    </span>
                  </div>
                  {pctOfBudget !== null && (
                    <div className="flex flex-col gap-1">
                      <div className="w-full h-2 bg-surface-container-high border border-on-background/10 overflow-hidden">
                        <div className={`h-full ${status === 'over' ? 'bg-error' : status === 'tight' ? 'bg-amber-500' : 'bg-emerald-500'}`}
                          style={{ width: `${Math.min(100, pctOfBudget)}%` }} />
                      </div>
                      <p className="text-[10px] text-on-surface-variant">{fmt(pctOfBudget)}% of safe budget</p>
                    </div>
                  )}
                  <button
                    onClick={() => setSelectedRate(isSelected ? null : rate)}
                    className={`mt-1 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest transition-colors ${isSelected ? 'bg-primary-container text-on-primary-container' : 'hover:bg-surface-container'}`}>
                    {isSelected ? '✓ Selected as draft rate' : 'Select as draft rate'}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {selectedRow && (
        <div className="border-4 border-primary-container bg-primary-container/10 p-5 flex flex-col gap-3"
          style={{ boxShadow: '5px 5px 0px 0px var(--neo-border-color)' }}>
          <h3 className="font-extrabold text-[13px] uppercase tracking-widest">Draft Conversion Rate</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-[12px]">
            <div><p className="text-on-surface-variant text-[10px] uppercase tracking-widest font-bold">Rate</p><p className="font-mono font-bold">{selectedRow.rate} : 1</p></div>
            <div><p className="text-on-surface-variant text-[10px] uppercase tracking-widest font-bold">Projected Payout</p><p className="font-mono font-bold">{fmt(selectedRow.projectedCpt)} CPT</p></div>
            <div><p className="text-on-surface-variant text-[10px] uppercase tracking-widest font-bold">Budget Used</p><p className="font-mono font-bold">{selectedRow.pctOfBudget !== null ? `${fmt(selectedRow.pctOfBudget)}%` : '—'}</p></div>
            <div><p className="text-on-surface-variant text-[10px] uppercase tracking-widest font-bold">Remaining Budget</p><p className="font-mono font-bold">{fmt(Math.max(0, budgetNum - selectedRow.projectedCpt))} CPT</p></div>
          </div>
          <button onClick={saveConversion} disabled={saving}
            className="mt-2 self-start px-5 py-2.5 bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Conversion Rate for This Season'}
          </button>
        </div>
      )}
    </div>
  )
}

function StatDisplay({ label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">{label}</p>
      <p className="font-mono font-extrabold text-[16px]">{value}</p>
    </div>
  )
}

function SimField({ label, value, onChange, suffix, note }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">{label}</label>
      <div className="relative">
        <input
          type="text" inputMode="decimal"
          value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-surface border-2 border-on-background pl-3 pr-14 py-2 font-mono text-[14px] focus:outline-none focus:border-primary-container"
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-on-surface-variant">{suffix}</span>}
      </div>
      {note && <p className="text-[10px] text-on-surface-variant">{note}</p>}
    </div>
  )
}